import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    FileText, Download, ExternalLink, LogOut, User,
    Clock, File, AlertCircle, Lock, Mail, Eye, EyeOff,
    ChevronLeft, Loader
} from 'lucide-react';
import { API_BASE } from './helpers';
import InvestorDataDashboard from './InvestorDataDashboard';

// ─── Investor Login Screen ─────────────────────────────────────────
export function InvestorLoginScreen({ onBack }) {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleLogin = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const res = await axios.post(`${API_BASE}/investor/login`, { email, password });
            sessionStorage.setItem('investorToken', res.data.token);
            sessionStorage.setItem('investorUser', JSON.stringify(res.data.user));
            window.location.reload();
        } catch (err) {
            const msg = err.response?.data?.error || 'Login failed. Please try again.';
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="crm-login">
            <div className="crm-login__card">
                <div className="crm-login__logo">
                    <img src="/cagan-logo.png" alt="Cagan Logo" className="crm-login__logo-img" />
                </div>
                <h1 className="crm-login__title">Investor Portal</h1>
                <p className="crm-login__subtitle">Cagan Management Group</p>

                <div className="crm-login__divider" />

                {error && (
                    <div className="investor-login__error">
                        <AlertCircle size={16} />
                        <span>{error}</span>
                    </div>
                )}

                <form onSubmit={handleLogin} className="investor-login__form">
                    <div className="investor-login__field">
                        <label className="investor-login__label">Email</label>
                        <div className="investor-login__input-wrap">
                            <Mail size={16} className="investor-login__input-icon" />
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@example.com"
                                className="investor-login__input"
                                required
                                autoComplete="email"
                            />
                        </div>
                    </div>

                    <div className="investor-login__field">
                        <label className="investor-login__label">Password</label>
                        <div className="investor-login__input-wrap">
                            <Lock size={16} className="investor-login__input-icon" />
                            <input
                                type={showPassword ? 'text' : 'password'}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Enter your password"
                                className="investor-login__input"
                                required
                                autoComplete="current-password"
                            />
                            <button
                                type="button"
                                className="investor-login__toggle-pw"
                                onClick={() => setShowPassword(!showPassword)}
                                tabIndex={-1}
                            >
                                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                            </button>
                        </div>
                    </div>

                    <button
                        type="submit"
                        className="crm-login__btn crm-login__btn--admin"
                        disabled={loading}
                    >
                        {loading ? <Loader size={18} className="investor-login__spinner" /> : <Lock size={18} />}
                        {loading ? 'Signing in...' : 'Sign In'}
                    </button>
                </form>

                <button className="investor-login__back" onClick={onBack}>
                    <ChevronLeft size={16} />
                    Back to login options
                </button>

                <p className="crm-login__footer-text">
                    Contact your account manager if you need access
                </p>
            </div>
        </div>
    );
}

// ─── Investor Dashboard ────────────────────────────────────────────
export function InvestorDashboard() {
    const [user, setUser] = useState(null);
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const token = sessionStorage.getItem('investorToken');
    const storedUser = JSON.parse(sessionStorage.getItem('investorUser') || 'null');

    const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadData = async () => {
        setLoading(true);
        setError('');
        try {
            const [profileRes, docsRes] = await Promise.all([
                axios.get(`${API_BASE}/investor/me`, authHeaders),
                axios.get(`${API_BASE}/investor/documents`, authHeaders)
            ]);
            setUser(profileRes.data);
            setDocuments(docsRes.data);
        } catch (err) {
            if (err.response?.status === 401) {
                handleLogout();
                return;
            }
            setError(err.response?.data?.error || 'Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        sessionStorage.removeItem('investorToken');
        sessionStorage.removeItem('investorUser');
        window.location.reload();
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return '\u2014';
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / 1048576).toFixed(1) + ' MB';
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '\u2014';
        return new Date(dateStr).toLocaleDateString('en-US', {
            month: 'short', day: 'numeric', year: 'numeric'
        });
    };

    const getFileIcon = (fileName) => {
        if (!fileName) return File;
        const ext = fileName.split('.').pop().toLowerCase();
        if (['pdf', 'doc', 'docx', 'xls', 'xlsx', 'csv'].includes(ext)) return FileText;
        return File;
    };

    const displayName = user
        ? `${user.FirstName} ${user.LastName}`
        : storedUser?.name || 'Investor';

    return (
        <div className="investor-portal">
            {/* Header */}
            <header className="investor-portal__header">
                <div className="investor-portal__header-left">
                    <img src="/cagan-logo.png" alt="Cagan Logo" className="investor-portal__logo" />
                    <div className="investor-portal__header-text">
                        <span className="investor-portal__brand">Cagan Management Group</span>
                        <span className="investor-portal__brand-sub">Investor Portal</span>
                    </div>
                </div>
                <div className="investor-portal__header-right">
                    <div className="investor-portal__user-info">
                        <User size={16} />
                        <span>{displayName}</span>
                    </div>
                    <button className="investor-portal__logout" onClick={handleLogout}>
                        <LogOut size={16} />
                        Sign Out
                    </button>
                </div>
            </header>

            {/* Main content */}
            <main className="investor-portal__main">
                <div className="investor-portal__welcome">
                    <h1>Welcome, {user?.FirstName || storedUser?.name?.split(' ')[0] || 'Investor'}</h1>
                    <p>Access your documents and investment materials below.</p>
                </div>

                {error && (
                    <div className="crm-error">
                        <AlertCircle size={18} />
                        <span>{error}</span>
                    </div>
                )}

                {/* Data Dashboard Widget — renders only if dashboard-flagged spreadsheets exist */}
                {!loading && (
                    <InvestorDataDashboard authHeaders={authHeaders} />
                )}

                {/* Documents Box */}
                <div className="investor-portal__card">
                    <div className="investor-portal__card-header">
                        <div className="investor-portal__card-title">
                            <FileText size={20} />
                            <h2>My Documents</h2>
                        </div>
                        <span className="investor-portal__card-count">
                            {documents.length} document{documents.length !== 1 ? 's' : ''}
                        </span>
                    </div>

                    <div className="investor-portal__card-body">
                        {loading ? (
                            <div className="investor-portal__loading">
                                <div className="crm-loading__spinner" />
                                <span>Loading documents...</span>
                            </div>
                        ) : documents.length === 0 ? (
                            <div className="investor-portal__empty">
                                <FileText size={40} />
                                <p>No documents available yet.</p>
                                <span>Documents shared with you will appear here.</span>
                            </div>
                        ) : (
                            <div className="investor-portal__doc-list">
                                {documents.map(doc => {
                                    const IconComponent = getFileIcon(doc.FileName);
                                    return (
                                        <div key={doc.DocumentId} className="investor-portal__doc-row">
                                            <div className="investor-portal__doc-icon">
                                                <IconComponent size={20} />
                                            </div>
                                            <div className="investor-portal__doc-info">
                                                <span className="investor-portal__doc-name">{doc.FileName}</span>
                                                <span className="investor-portal__doc-meta">
                                                    {doc.DocumentType && <span className="investor-portal__doc-type">{doc.DocumentType}</span>}
                                                    <span>{formatFileSize(doc.FileSize)}</span>
                                                    <span><Clock size={12} /> {formatDate(doc.UploadedDate)}</span>
                                                </span>
                                                {doc.Notes && (
                                                    <span className="investor-portal__doc-notes">{doc.Notes}</span>
                                                )}
                                            </div>
                                            <div className="investor-portal__doc-actions">
                                                {doc.FilePath && (
                                                    <a
                                                        href={`${API_BASE.replace('/api', '')}${doc.FilePath}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="investor-portal__doc-btn"
                                                        title="Download"
                                                    >
                                                        <Download size={16} />
                                                    </a>
                                                )}
                                                {doc.ExternalUrl && (
                                                    <a
                                                        href={doc.ExternalUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="investor-portal__doc-btn"
                                                        title="Open link"
                                                    >
                                                        <ExternalLink size={16} />
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </main>

            {/* Footer */}
            <footer className="investor-portal__footer">
                <p>&copy; {new Date().getFullYear()} Cagan Management Group. All rights reserved.</p>
            </footer>
        </div>
    );
}
